import { Component, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTicket, faPassport, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { Router, RouterLink } from "@angular/router";
import { debounce, form, FormField, FormRoot, maxLength, minLength, pattern, required } from "@angular/forms/signals";
import { UserStateService } from '../services/user-state-service';
import { catchError, map, max } from 'rxjs';
import { NgxMaskDirective } from 'ngx-mask';
import { IsValidGQL } from '../../generated/graphql';
import { Apollo } from 'apollo-angular';

interface LoginData {
  kassenzeichen: string;
}

@Component({
  selector: 'app-login-page',
  imports: [
    FontAwesomeModule,
    RouterLink,
    FormField,
    NgxMaskDirective,
],
  templateUrl: './login-page.html',
  styleUrl: './login-page.css',
})
export class LoginPage {

  readonly faTicket = faTicket;
  readonly faPassport = faPassport;
  readonly faSpinner = faSpinner;

  private readonly isValidGQL = inject(IsValidGQL);
  private readonly userStateService = inject(UserStateService);
  private readonly router = inject(Router);
  private readonly apollo = inject(Apollo);

  readonly loginModel = signal<LoginData>({
    kassenzeichen: '',
  });

  readonly errorMessage = signal<string | null>(null);
  readonly isLoading = signal(false);

  readonly loginForm = form(this.loginModel, (scheamPath) => {
    debounce(scheamPath.kassenzeichen, 200);
    required(scheamPath.kassenzeichen);
    pattern(scheamPath.kassenzeichen, /^\d{14}$/);
  });

  onSubmit(event: Event) {
    event.preventDefault();

    this.userStateService.resetUserState();

    this.userStateService.userStateSignal.update((state) => ({
      ...state,
      kassenzeichen: this.loginModel().kassenzeichen,
    }));

    this.isLoading.set(true);
    this.apollo.client.clearStore().finally(() => {
      this.isValidGQL.fetch({
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      }).pipe(
        map(result => result?.data?.isValid),
        catchError((error) => {
          console.error('Error validating kassenzeichen:', error);
          this.errorMessage.set("Es ist ein Fehler bei der Validierung des Kassenzeichens aufgetreten. Bitte versuchen Sie es später erneut.");
          this.isLoading.set(false);
          return [false];
        })
      ).subscribe((result) => {
        this.isLoading.set(false);
        console.debug('Validation result for kassenzeichen', this.loginModel().kassenzeichen, ':', result);
        if(result === true) {
          this.userStateService.userStateSignal.update((state) => ({
            ...state,
            kassenzeichenValidated: true,
          }));
          this.router.navigate(["/home"]);
        } else {
          this.errorMessage.set("Das eingegebene Kassenzeichen ist ungültig. Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut.");
        }
      });
    });
  }
}
